import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

type GenericFormState = {
    schemaByNodeId: Record<string, any>;
    draftByKey: Record<string, Record<string, any>>;
    didRehydrateByKey: Record<string, boolean>;
    baselineHashByKey: Record<string, string | null>;
};


const initialState: GenericFormState = {
    schemaByNodeId: {},
    draftByKey: {},

    didRehydrateByKey: {},
    baselineHashByKey: {},
};

function ensureMaps(state: GenericFormState) {
    state.schemaByNodeId ??= {};
    state.draftByKey ??= {};
    state.didRehydrateByKey ??= {};
    state.baselineHashByKey ??= {};
}

export const genericFormSlice = createSlice({
    name: "genericForm",
    initialState,
    reducers: {
        setSchema(
            state,
            action: PayloadAction<{ nodeId: string; schema: Record<string, any> }>,
        ) {
            state.schemaByNodeId[action.payload.nodeId] = action.payload.schema;
        },

        ensureDraft(state, action: PayloadAction<{ draftKey: string }>) {
            const { draftKey } = action.payload;

            state.draftByKey ??= {};
            state.didRehydrateByKey ??= {};
            state.baselineHashByKey ??= {};

            state.draftByKey[draftKey] ??= {};
        },


        setDraft(
            state,
            action: PayloadAction<{
                draftKey: string;
                values: Record<string, any>;
            }>,
        ) {
            ensureMaps(state);
            state.draftByKey[action.payload.draftKey] = action.payload.values;
        },

        patchDraft(
            state,
            action: PayloadAction<{
                draftKey: string;
                patch: Record<string, any>;
            }>,
        ) {
            ensureMaps(state);
            const { draftKey, patch } = action.payload;
            state.draftByKey[draftKey] = {
                ...(state.draftByKey[draftKey] ?? {}),
                ...patch,
            };
        },

        clearDraft(state, action: PayloadAction<{ draftKey: string }>) {
            ensureMaps(state);
            delete state.draftByKey[action.payload.draftKey];
        },
        setRehydrateBaseline(
            state,
            action: PayloadAction<{
                draftKey: string;
                didRehydrate: boolean;
                baselineHash: string | null;
            }>,
        ) {
            ensureMaps(state);
            const { draftKey, didRehydrate, baselineHash } = action.payload;
            state.didRehydrateByKey[draftKey] = didRehydrate;
            state.baselineHashByKey[draftKey] = baselineHash;
        },

        clearBaseline(state, action: PayloadAction<{ draftKey: string }>) {
            ensureMaps(state);
            const { draftKey } = action.payload;
            delete state.didRehydrateByKey[draftKey];
            delete state.baselineHashByKey[draftKey];
        },

    },
});

export const {
    setSchema,
    ensureDraft,
    setDraft,
    patchDraft,
    clearDraft,
    setRehydrateBaseline,
    clearBaseline,
} = genericFormSlice.actions;


export default genericFormSlice.reducer;

const EMPTY_OBJ: Record<string, any> = {};
const EMPTY_SCHEMA: Record<string, any> = {};

export const selectSchemaByNodeId = (nodeId: string) => (s: any) =>
    s.genericForm?.schemaByNodeId?.[nodeId] ?? EMPTY_SCHEMA;

export const selectDraftByKey = (draftKey: string) => (s: any) =>
    s.genericForm?.draftByKey?.[draftKey] ?? EMPTY_OBJ;

export const selectDidRehydrateByKey = (draftKey: string) => (s: any) =>
    !!s.genericForm?.didRehydrateByKey?.[draftKey];

export const selectBaselineHashByKey = (draftKey: string) => (s: any) =>
    s.genericForm?.baselineHashByKey?.[draftKey] ?? null;



